import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  ChatCircleDots,
  Cube,
  EnvelopeSimple,
  FilePdf,
  Heart,
  Images,
  MagnifyingGlassPlus,
  Ruler,
  ShareNetwork,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import { PortalLightbox } from "@/components/PortalLightbox";
import { PhotoStrip } from "@/components/site/PhotoStrip";
import { PortalReserveDialog } from "@/components/PortalReserveDialog";
import { RemnantShapeDims } from "@/components/RemnantShapeDims";
import { Eyebrow } from "@/components/site/Blocks";
import { Rule } from "@/components/site/Motion";
import { LineTabs } from "@/components/site/Shop";
import { Slab3D } from "@/components/site/Slab3D";
import { SlabMeasure } from "@/components/site/SlabMeasure";
import { portalApi, portalAsset, idFromSlug } from "@/lib/portalApi";
import { SaveCutDialog } from "@/components/portal/SaveCutDialog";
import { API } from "@/lib/api";
import { blurbFor, careFor } from "@/lib/stoneCopy";
import { toggleWishlist, useWishlist } from "@/lib/wishlist";

const Spec = ({ label, value, testId }) => (
  <div className="flex items-baseline justify-between gap-6 border-b border-[color:var(--s-line)] py-3.5" data-testid={testId}>
    <dt className="s-micro !text-[10px] text-[color:var(--s-mute)]">{label}</dt>
    <dd className="text-right text-base leading-snug text-[color:var(--s-ink)]">{value}</dd>
  </div>
);

// "Will it fit?" helper for remnants: check a typed size (rotated to fit), show a nesting
// preview, and let the customer hold the remnant for one of their own job pieces in one tap.
const RemnantNest = ({ item, pl, pw }) => {
  const L = Number(item.length_in) || 0;
  const W = Number(item.width_in) || 0;
  const svgRef = useRef(null);
  const big = Math.max(L, W) || 1;
  const pad = big * 0.06;
  const poly = (item.polygon || [])
    .map((p) => ({ x: Number(p.x ?? p[0] ?? 0) * L, y: Number(p.y ?? p[1] ?? 0) * W }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  const pieceOn = pl > 0 && pw > 0;
  const w = Math.min(Math.max(pl, pw), L || 1);
  const h = Math.min(Math.min(pl, pw), W || 1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  useEffect(() => {
    setPos({ x: 0, y: 0 });
  }, [pl, pw, item.id]);
  if (!(L > 0 && W > 0)) return null;
  const clampX = (x) => Math.max(0, Math.min(x, Math.max(0, L - w)));
  const clampY = (y) => Math.max(0, Math.min(y, Math.max(0, W - h)));
  const toSvg = (cx, cy) => {
    const r = svgRef.current.getBoundingClientRect();
    return {
      x: ((cx - r.left) / r.width) * (L + pad * 2) - pad,
      y: ((cy - r.top) / r.height) * (W + pad * 2) - pad,
    };
  };
  const onDown = (e) => {
    if (!pieceOn) return;
    e.preventDefault();
    const start = toSvg(e.clientX, e.clientY);
    dragRef.current = { ox: start.x - pos.x, oy: start.y - pos.y };
    const move = (ev) => {
      const q = toSvg(ev.clientX, ev.clientY);
      setPos({ x: clampX(q.x - dragRef.current.ox), y: clampY(q.y - dragRef.current.oy) });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const sides = poly.map((p, i) => {
    const q = poly[(i + 1) % poly.length];
    return { len: Math.round(Math.hypot(q.x - p.x, q.y - p.y)), mx: (p.x + q.x) / 2, my: (p.y + q.y) / 2 };
  });
  const slabPath = item.slab_photo || item.photo || "";
  const slabSrc = slabPath ? portalAsset(`/inventory/photo/${slabPath}?w=1400`) : "";
  const clipId = `nest-clip-${item.id}`;
  return (
    <div className="mt-3 overflow-hidden rounded-lg border border-[color:var(--s-line)] bg-[#141414]">
      <svg
        ref={svgRef}
        viewBox={`${-pad} ${-pad} ${L + pad * 2} ${W + pad * 2}`}
        className="block w-full touch-none"
        data-testid="portal-fit-nest"
        style={{ maxHeight: 420 }}
      >
        <defs>
          <clipPath id={clipId}>
            {poly.length > 2 ? (
              <polygon points={poly.map((p) => `${p.x},${p.y}`).join(" ")} />
            ) : (
              <rect x="0" y="0" width={L} height={W} />
            )}
          </clipPath>
        </defs>
        {slabSrc && (
          <image
            href={slabSrc}
            x="0"
            y="0"
            width={L}
            height={W}
            preserveAspectRatio="xMidYMid slice"
            opacity="0.45"
            clipPath={`url(#${clipId})`}
            data-testid="portal-fit-slab-img"
          />
        )}
        <rect x="0" y="0" width={L} height={W} fill="none" stroke="#c39e6d" strokeOpacity="0.4" strokeWidth={big * 0.004} strokeDasharray={`${big * 0.02} ${big * 0.013}`} />
        {poly.length > 2 && (
          <polygon points={poly.map((p) => `${p.x},${p.y}`).join(" ")} fill="rgba(195,158,109,0.12)" stroke="#c39e6d" strokeWidth={big * 0.006} strokeLinejoin="round" />
        )}
        {sides.map((sd, i) => (
          <text
            key={i}
            x={sd.mx}
            y={sd.my}
            fontSize={big * 0.045}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#e6c78f"
            fontWeight="700"
            data-testid={`portal-fit-side-${i}`}
            style={{ paintOrder: "stroke", stroke: "#141414", strokeWidth: big * 0.012 }}
          >
            {sd.len}&quot;
          </text>
        ))}
        {pieceOn && (
          <g onPointerDown={onDown} style={{ cursor: "grab" }} data-testid="portal-fit-piece">
            <rect x={pos.x} y={pos.y} width={w} height={h} fill="rgba(16,185,129,0.42)" stroke="#059669" strokeWidth={big * 0.006} rx={big * 0.004} />
            <text x={pos.x + w / 2} y={pos.y + h / 2} fontSize={big * 0.05} textAnchor="middle" dominantBaseline="central" fill="#eafff5" fontWeight="700" style={{ pointerEvents: "none" }}>
              {Math.round(Math.max(pl, pw))}×{Math.round(Math.min(pl, pw))}
            </text>
          </g>
        )}
      </svg>
      {pieceOn && (
        <div className="px-3 pb-2 pt-1 text-center text-[10px] text-[color:var(--s-mute)]" data-testid="portal-fit-drag-hint">
          Drag the green piece to position it on the slab.
        </div>
      )}
    </div>
  );
};

const RemnantFit = ({ item, onHold }) => {
  const [l, setL] = useState("");
  const [w, setW] = useState("");
  const rL = Math.max(Number(item.length_in) || 0, Number(item.width_in) || 0);
  const rW = Math.min(Number(item.length_in) || 0, Number(item.width_in) || 0);
  const pl = parseFloat(l);
  const pw = parseFloat(w);
  const valid = pl > 0 && pw > 0 && rL > 0 && rW > 0;
  const fits = valid && Math.max(pl, pw) <= rL + 0.02 && Math.min(pl, pw) <= rW + 0.02;
  const mine = item.fits || [];
  const canHold = item.available && !item.requested;
  const inputCls =
    "h-10 w-24 border border-[color:var(--s-line)] bg-[color:var(--s-surface)] px-2 text-sm text-[color:var(--s-ink)] outline-none focus:border-[color:var(--s-ink)]";
  const pickPiece = (f) => {
    setL(String(f.l ?? ""));
    setW(String(f.w ?? ""));
  };
  return (
    <div className="mt-6 border border-[color:var(--s-line)] bg-white p-4" data-testid="portal-item-fit">
      <div className="s-micro !text-[10px] text-[color:var(--s-mute)]">Will it fit?</div>
      {rL > 0 && rW > 0 && (
        <p className="mt-2 text-xs leading-relaxed text-[color:var(--s-mute)]">
          Overall size {Math.round(rL)}&quot; × {Math.round(rW)}&quot;. Type a piece size — we rotate it to check.
        </p>
      )}
      <div className="mt-3 flex items-center gap-2">
        <input data-testid="portal-fit-length" value={l} onChange={(e) => setL(e.target.value)} inputMode="decimal" placeholder="Length" className={inputCls} />
        <span className="text-[color:var(--s-mute)]">×</span>
        <input data-testid="portal-fit-width" value={w} onChange={(e) => setW(e.target.value)} inputMode="decimal" placeholder="Width" className={inputCls} />
        <span className="text-sm text-[color:var(--s-mute)]">in</span>
      </div>
      {valid && (
        <div
          data-testid="portal-fit-result"
          className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${fits ? "bg-emerald-500/15 text-emerald-600" : "bg-red-500/15 text-red-600"}`}
        >
          {fits ? "✓ Fits this remnant" : "✗ Too big for this remnant"}
        </div>
      )}
      {valid && fits && (
        <>
          <RemnantNest item={item} pl={pl} pw={pw} />
          <p className="mt-1 text-[10px] text-[color:var(--s-mute)]">Nesting preview — drag to position. Approximate only; we confirm before cutting.</p>
        </>
      )}
      {mine.length > 0 && (
        <div className="mt-4 border-t border-[color:var(--s-line)] pt-3" data-testid="portal-fit-mine">
          <div className="s-micro !text-[10px] text-[color:var(--s-mute)]">Fits pieces from your projects</div>
          <ul className="mt-2 space-y-2">
            {mine.map((f, i) => (
              <li key={i} data-testid={`portal-fit-piece-${i}`} className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  data-testid={`portal-fit-piece-pick-${i}`}
                  onClick={() => pickPiece(f)}
                  className="min-w-0 text-left text-sm"
                  title="Preview this piece in the remnant"
                >
                  <span className="truncate text-[color:var(--s-ink)] underline decoration-dotted underline-offset-2">
                    {f.label}
                  </span>
                  {f.area ? <span className="text-[color:var(--s-mute)]"> · {f.area}</span> : null}
                  {f.quote ? <span className="text-[color:var(--s-mute)]"> · {f.quote}</span> : null}
                  <span className="s-num ml-2 text-[color:var(--s-mute)]">{f.size}</span>
                </button>
                {canHold && (
                  <button
                    type="button"
                    data-testid={`portal-fit-hold-${i}`}
                    onClick={() => {
                      pickPiece(f);
                      onHold?.(f);
                    }}
                    className="s-act s-act-gold shrink-0"
                  >
                    Hold for this
                  </button>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[10px] leading-relaxed text-[color:var(--s-mute)]">
            Approximate — based on overall size, not the exact cut shape. We&apos;ll confirm before cutting.
          </p>
        </div>
      )}
    </div>
  );
};

const TextAct = ({ href, onClick, icon: Icon, testId, children }) => {
  const cls = "s-micro inline-flex items-center gap-2 !text-[11px] text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]";
  const inner = (
    <>
      <Icon size={14} weight="bold" className="text-[color:var(--s-gold)]" /> {children}
    </>
  );
  return href ? (
    <a href={href} data-testid={testId} className={cls}>{inner}</a>
  ) : (
    <button type="button" onClick={onClick} data-testid={testId} className={cls}>{inner}</button>
  );
};

export default function PortalInventoryItem() {
  const { slug } = useParams();
  const id = idFromSlug(slug);
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [pricing, setPricing] = useState({ show: false });
  const [loading, setLoading] = useState(true);
  const [reserve, setReserve] = useState(false);
  const [reservePrefill, setReservePrefill] = useState(null);
  const [light, setLight] = useState(-1);
  const [active, setActive] = useState(0);
  const [tab, setTab] = useState("description");
  const [view3d, setView3d] = useState(false);
  const [measure, setMeasure] = useState(null);
  const [saveCut, setSaveCut] = useState(null);
  const [projects, setProjects] = useState([]);
  const [slabShot, setSlabShot] = useState(null);
  const wishlist = useWishlist();

  useEffect(() => {
    portalApi
      .get("/projects")
      .then(({ data }) => setProjects(Array.isArray(data) ? data : []))
      .catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    setActive(0);
    setTab("description");
    portalApi
      .get(`/inventory/item/${id}`)
      .then(({ data }) => {
        setItem(data.item);
        setPricing(data.pricing || { show: false });
      })
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [id]);

  const shots = useMemo(
    () =>
      (item?.photos || []).map((p, n) => ({
        path: p.path,
        thumb: p.thumb,
        title: item.color,
        sub: `${item.kind}${item.size ? ` · ${item.size}` : ""}${(item.photos || []).length > 1 ? ` · photo ${n + 1} of ${item.photos.length}` : ""}`,
      })),
    [item],
  );

  if (loading)
    return (
      <PortalShell back="/portal/inventory">
        <div className="py-24 text-center text-sm text-slate-500" data-testid="portal-item-loading">Loading…</div>
      </PortalShell>
    );

  if (!item)
    return (
      <PortalShell back="/portal/inventory">
        <div className="py-24 text-center" data-testid="portal-item-missing">
          <p className="text-sm text-slate-500">This piece is no longer listed.</p>
          <button type="button" onClick={() => navigate("/portal/inventory")} className="mt-3 text-sm font-semibold text-[#c39e6d] underline">
            Back to inventory
          </button>
        </div>
      </PortalShell>
    );

  const photos = item.photos || [];
  const hero = photos[active]?.path || item.photo;
  const src = (path, w) => portalAsset(`/inventory/photo/${path}${w ? `?w=${w}` : ""}`);
  const hasSize = item.length_in > 0 && item.width_in > 0;
  const stock = item.kind === "Remnant" ? "1 available" : item.qty > 0 ? `${item.qty} in stock` : "Made to order";
  const showPrice = pricing.show && item.price_per_sqft != null;
  const quoteHref =
    `/portal/request?slab=${item.id}` +
    `&color=${encodeURIComponent(item.color || "")}` +
    `&item=${encodeURIComponent(item.code || "")}` +
    `&size=${encodeURIComponent(hasSize ? item.size : "")}` +
    `&area=${encodeURIComponent(item.sqft || "")}`;
  const inquireHref = `mailto:?subject=${encodeURIComponent(`Slab enquiry — ${item.color || ""}`)}&body=${encodeURIComponent(
    `I'd like more information on ${item.color || "this slab"}${item.code ? ` (item ${item.code})` : ""}${hasSize ? `, ${item.size}` : ""}.`,
  )}`;
  const materialHref = item.material ? `/portal/inventory?material=${encodeURIComponent(item.material)}` : "/portal/inventory";
  const rows = item.available_slabs || [];
  const lots = rows.reduce((acc, s) => {
    const lot = s.lot || "Other";
    (acc[lot] = acc[lot] || []).push(s);
    return acc;
  }, {});

  const holdForPiece = (f) => {
    setReservePrefill({
      project: f.area || "",
      note: `For ${f.label}${f.size ? ` (${f.size})` : ""}${f.quote ? ` on ${f.quote}` : ""}.`,
    });
    setReserve(true);
  };
  const share = async () => {    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: item.color || "Slab", url });
        return;
      }
    } catch {
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.message(url);
    }
  };
  const saved = new Set(wishlist.map((w) => w.id));
  const wish = (s) => {
    const on = toggleWishlist({ id: s.id, key: item.key, color: item.color, item_no: s.item_no, size: s.size, thumb: s.thumb });
    toast.success(on ? "Saved to your wishlist" : "Removed from your wishlist");
  };
  const requestSlab = (s, piece) => {
    const params = new URLSearchParams({
      slab: item.id,
      color: item.color || "",
      item: s.item_no || item.code || "",
      size: piece ? `${piece.length}" x ${piece.width}"` : s.size || "",
      length: String(piece ? piece.length : ""),
      width: String(piece ? piece.width : ""),
      area: String(piece ? piece.area : s.sqft || ""),
      photo: s.photo || item.photo || "",
    });
    navigate(`/portal/request?${params.toString()}`);
  };
  const zoomSlab = (s, index = 0) => {
    const pics = (s.photos || []).length ? s.photos : s.thumb || s.photo ? [{ path: s.photo || item.photo, thumb: s.thumb }] : [];
    if (!pics.length) return;
    const facts = [s.material || item.material, s.size].filter(Boolean).join(" · ");
    setSlabShot({
      index: Math.min(index, pics.length - 1),
      shots: pics.map((p, n) => ({
        path: p.path,
        thumb: p.thumb,
        title: `${item.color} · Item ${s.item_no || "—"}`,
        sub: pics.length > 1 ? `${facts} · photo ${n + 1} of ${pics.length}` : facts,
      })),
    });
  };
  const slabMail = (s) =>
    `mailto:?subject=${encodeURIComponent(`Slab enquiry — ${item.color || ""} (item ${s.item_no || ""})`)}&body=${encodeURIComponent(
      `I'd like more information on ${item.color || "this slab"} (item ${s.item_no || ""}${s.size ? `, ${s.size}` : ""}).`,
    )}`;
  const tabs = [
    { id: "description", label: "Description" },
    { id: "care", label: "Care & cleaning" },
  ];

  return (
    <PortalShell>
      <div className="site s-flat -mx-4 bg-[color:var(--s-bg)] px-4 py-6 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10" data-testid="portal-inventory-item">
        <Rule className="is-in" />
        <div className="flex flex-wrap items-center justify-between gap-4 py-5">
          <Link to="/portal/inventory" data-testid="portal-item-back" className="s-micro inline-flex items-center gap-2 !text-[11px] text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]">
            <ArrowLeft size={13} weight="bold" /> All inventory
          </Link>
          <nav className="s-micro flex min-w-0 items-center gap-2 !text-[10px] text-[color:var(--s-mute)]" aria-label="Breadcrumb" data-testid="portal-item-crumbs">
            <Link to="/portal/inventory" className="hover:text-[color:var(--s-ink)]">Inventory</Link>
            {item.material && (
              <>
                <span aria-hidden="true">/</span>
                <Link to={materialHref} className="hover:text-[color:var(--s-ink)]">{item.material}</Link>
              </>
            )}
            <span aria-hidden="true">/</span>
            <span className="max-w-[14rem] truncate text-[color:var(--s-ink)]">{item.color}</span>
          </nav>
        </div>
        <Rule className="is-in" />

        <div className="mt-12 grid gap-12 lg:grid-cols-2 lg:items-start lg:gap-16 xl:grid-cols-[1.1fr_1fr]">
          {/* sticky gallery column */}
          <div className="min-w-0 lg:sticky lg:top-24" data-testid="portal-item-gallery-col">
            <div className="s-fig relative bg-[color:var(--s-line)]">
              {hero ? (
                <button type="button" data-testid="portal-item-photo" onClick={() => setLight(active)} className="group relative block w-full cursor-zoom-in">
                  <img key={hero} src={src(hero, 1600)} alt={item.color} className="s-fade aspect-[4/3] w-full object-cover" />
                  <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center bg-white/94 text-[color:var(--s-ink)] transition-colors duration-300 group-hover:bg-[color:var(--s-ink)] group-hover:text-white">
                    <MagnifyingGlassPlus size={16} weight="bold" />
                  </span>
                </button>
              ) : (
                <div className="flex aspect-[4/3] items-center justify-center text-sm text-[color:var(--s-mute)]">Photo coming soon</div>
              )}
              <span className="absolute left-4 top-4 flex flex-wrap items-start gap-1.5">
                {item.on_sale && <span data-testid="portal-item-badge-sale" className="s-badge s-badge-gold">Sale</span>}
                {item.new_arrival && <span data-testid="portal-item-badge-new" className="s-badge">New</span>}
                {item.kind === "Remnant" && <span className="s-badge">Remnant</span>}
                {item.requested && <span data-testid="portal-item-badge-requested" className="s-badge s-badge-light">Requested</span>}
              </span>
            </div>
            {photos.length > 1 && (
              <div className="mt-3 grid grid-cols-4 gap-3 sm:grid-cols-5" data-testid="portal-item-thumbs">
                {photos.map((p, n) => (
                  <button
                    key={p.path}
                    type="button"
                    data-testid={`portal-item-thumb-${n}`}
                    onClick={() => setActive(n)}
                    aria-pressed={n === active}
                    className={`s-fig aspect-[4/3] bg-[#eeece7] transition-opacity duration-300 ${n === active ? "outline outline-1 outline-offset-2 outline-[color:var(--s-ink)]" : "opacity-60 hover:opacity-100"}`}
                  >
                    <img src={src(p.thumb || p.path, 400)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-4 text-xs text-[color:var(--s-mute)]">
              <span data-testid="portal-item-photo-count">{photos.length ? `${active + 1} / ${photos.length}` : ""}</span>
              <div className="flex flex-wrap gap-6">
                <TextAct testId="portal-item-gallery" onClick={() => photos.length && setLight(active)} icon={Images}>Fullscreen</TextAct>
                {item.photo && <TextAct testId="portal-item-3d" onClick={() => setView3d(true)} icon={Cube}>3D view</TextAct>}
                {hasSize && item.kind !== "Remnant" && (
                  <TextAct testId="portal-item-measure-action" onClick={() => setMeasure(item)} icon={Ruler}>Measure</TextAct>
                )}
                <TextAct testId="portal-item-share" onClick={share} icon={ShareNetwork}>Share</TextAct>
              </div>
            </div>
            {item.kind === "Remnant" && item.length_in > 0 && item.width_in > 0 && (
              <div className="mt-6 border border-[color:var(--s-line)] bg-white p-4" data-testid="portal-item-measure">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[color:var(--s-mute)]">Measure</div>
                  <button
                    type="button"
                    data-testid="portal-item-measure-open"
                    onClick={() => setMeasure(item)}
                    className="s-act s-act-gold inline-flex items-center gap-1.5"
                  >
                    <Ruler size={14} weight="bold" /> View larger
                  </button>
                </div>
                <button
                  type="button"
                  data-testid="portal-item-measure-preview"
                  onClick={() => setMeasure(item)}
                  title="Open the full measure view"
                  className="mt-3 block w-full cursor-zoom-in"
                >
                  {(item.polygon || []).length > 2 ? (
                    <RemnantShapeDims polygon={item.polygon} length={item.length_in} width={item.width_in} testId="portal-item-shape" />
                  ) : (
                    <div className="font-mono-data flex aspect-[2/1] w-full items-center justify-center border border-dashed border-[color:var(--s-line)] text-xs text-[color:var(--s-mute)]" data-testid="portal-item-shape">
                      {Math.round(item.length_in)}&quot; × {Math.round(item.width_in)}&quot;
                    </div>
                  )}
                </button>
                <p className="mt-2 text-[10px] text-[color:var(--s-mute)]">Tap to size a cut piece — each side in inches.</p>
              </div>
            )}
          </div>

          {/* summary column */}
          <div className="min-w-0" data-testid="portal-item-summary">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="s-num text-3xl sm:text-4xl" data-testid={showPrice ? "portal-item-price" : "portal-item-area"}>
                {showPrice ? `$${item.price_per_sqft.toFixed(2)}` : item.sqft ? `${item.sqft} ft²` : item.thickness || item.kind}
              </span>
              <span className="s-micro !text-[10px] text-[color:var(--s-mute)]" data-testid="portal-item-stock">
                {showPrice
                  ? `per ft² · ${stock}`
                  : pricing.show
                    ? `Price on request · ${stock}`
                    : stock}
              </span>
            </div>
            <div className="mt-6">
              <Eyebrow>{[item.brand, item.kind, item.code ? `#${item.code}` : ""].filter(Boolean).join(" · ") || "Slab"}</Eyebrow>
            </div>
            <h1 className="s-hx mt-4 break-words text-4xl sm:text-5xl lg:text-6xl" data-testid="portal-item-name">
              {item.color || "Unnamed"}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[color:var(--s-mute)]" data-testid="portal-item-blurb">
              {item.description || blurbFor(item)}
            </p>

            <div className="mt-9 grid gap-6 sm:grid-cols-2" data-testid="portal-item-actions">
              <button type="button" data-testid="portal-item-get-quote" onClick={() => navigate(quoteHref)} className="s-btn w-full">
                Get a quote
              </button>
              {item.requested ? (
                <span className="s-ghost s-no-arrow w-full !bg-emerald-700" data-testid="portal-item-requested">
                  <CheckCircle size={16} weight="bold" /> Hold requested
                </span>
              ) : item.available ? (
                <button type="button" data-testid="portal-item-reserve" onClick={() => { setReservePrefill(null); setReserve(true); }} className="s-ghost w-full">
                  Ask us to hold it
                </button>
              ) : (
                <span className="s-ghost s-no-arrow w-full opacity-60" data-testid="portal-item-unavailable">No longer available</span>
              )}
            </div>

            <div className="mt-9 flex flex-wrap gap-x-7 gap-y-3 border-y border-[color:var(--s-line)] py-4" data-testid="portal-item-links">
              <TextAct href={inquireHref} testId="portal-item-inquire" icon={EnvelopeSimple}>Inquire</TextAct>
              {item.photo && <TextAct testId="portal-item-3d-link" onClick={() => setView3d(true)} icon={Cube}>3D view</TextAct>}
              <TextAct testId="portal-item-share-link" onClick={share} icon={ShareNetwork}>Share</TextAct>
            </div>

            <div className="mt-10">
              <h2 className="s-micro !text-[11px] text-[color:var(--s-ink)]">Specifications</h2>
              <dl className="mt-2 border-t border-[color:var(--s-line)]" data-testid="portal-item-specs">
                {item.brand && <Spec label="Brand" value={item.brand} />}
                <Spec label="Material" value={item.material || item.kind} />
                <Spec label="Finish" value={item.finish || "—"} />
                <Spec label="Thickness" value={item.thickness || "—"} />
                <Spec label="Size" value={hasSize ? item.size : "On request"} testId="portal-item-size" />
                {item.sqft > 0 && <Spec label={item.kind === "Remnant" ? "Usable area" : "Area"} value={`${item.sqft} ft²`} />}
                {item.code && <Spec label="Stock #" value={item.code} />}
                <Spec label="Availability" value={stock} />
                {showPrice && <Spec label={pricing.label || "Your price"} value={`$${item.price_per_sqft.toFixed(2)} per ft²`} testId="portal-item-price-row" />}
              </dl>
            </div>
          </div>
        </div>

        <section className="mt-24" data-testid="portal-item-tabs-section">
          <LineTabs items={tabs} value={tab} onChange={setTab} testIdPrefix="portal-item-tab" tablist />
          <div className="mt-10 max-w-3xl" role="tabpanel" data-testid={`portal-item-panel-${tab}`}>
            {tab === "description" && (
              <div className="space-y-5 text-base leading-relaxed text-[color:var(--s-mute)]">
                <p>{item.description || blurbFor(item)}</p>
                <p>
                  Every slab we sell is templated by our own crew, cut in-house and installed by the same people — no middle shop, no surprises
                  between the slab you picked and the counter you get.
                </p>
                {item.available && !item.requested && <p>Like it? Ask us to hold it and we&apos;ll tag the slab with your name while you decide.</p>}
              </div>
            )}
            {tab === "care" && (
              <div className="space-y-5 text-base leading-relaxed text-[color:var(--s-mute)]">
                <p>{careFor(item.material)}</p>
                <p>Questions about a specific spill or stain? Text us a photo and we&apos;ll tell you exactly what to use.</p>
              </div>
            )}
          </div>
        </section>

        {rows.length > 0 && (
          <section className="mt-24" data-testid="portal-available-slabs">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <Eyebrow>Inventory</Eyebrow>
                <h2 className="s-hx mt-5 text-4xl sm:text-5xl">Available slabs.</h2>
              </div>
              <span className="text-sm text-[color:var(--s-mute)]" data-testid="portal-available-count">
                {rows.length} {rows.length === 1 ? "piece" : "pieces"} on the floor
              </span>
            </div>
            <Rule className="mt-8 is-in" />
            <div className="mt-8 grid grid-cols-[minmax(0,1fr)] gap-4">
              {Object.entries(lots).map(([lot, list]) => (
                <Fragment key={lot}>
                  {Object.keys(lots).length > 1 && (
                    <div className="s-lot" data-testid={`portal-item-lot-${lot.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>Lot: {lot}</div>
                  )}
                  {list.map((s) => {
                    const n = rows.indexOf(s);
                    const canMeasure = s.length_in > 0 && s.width_in > 0;
                    const isSaved = saved.has(s.id);
                    return (
                      <div key={`${s.item_no}-${n}`} data-testid={`portal-available-slab-${n}`} className="flex flex-col gap-4 border-b border-[color:var(--s-line)] pb-5 lg:flex-row lg:items-center lg:gap-6">
                        {(s.photos || []).length > 1 ? (
                          <div className="h-24 w-full shrink-0 bg-[#eeece7] lg:h-20 lg:w-32">
                            <PhotoStrip photos={s.photos} srcOf={(path) => src(path, 200)} onOpen={(i) => zoomSlab(s, i)} testPrefix={`portal-available-slab-zoom-${n}`} />
                          </div>
                        ) : (
                          <button
                            type="button"
                            data-testid={`portal-available-slab-zoom-${n}`}
                            onClick={() => zoomSlab(s, 0)}
                            disabled={!(s.thumb || s.photo)}
                            title="Zoom the slab photo"
                            className="h-24 w-full shrink-0 cursor-zoom-in bg-[#eeece7] disabled:cursor-default lg:h-20 lg:w-28"
                          >
                            {s.thumb ? <img src={src(s.thumb, 200)} alt="" className="h-full w-full object-cover" /> : null}
                          </button>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-3">
                            <span className="truncate text-lg font-medium tracking-tight text-[color:var(--s-ink)]">Item {s.item_no || "—"}</span>
                            {s.lot && <span className="s-micro !text-[10px] text-[color:var(--s-mute)]">Lot {s.lot}</span>}
                          </div>
                          <div className="mt-1 text-sm text-[color:var(--s-mute)]">
                            {[s.material, s.thickness, s.length_in > 0 && s.width_in > 0 ? `${s.size}${s.sqft ? ` · ${s.sqft} ft²` : ""}` : "Size on request"].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 lg:shrink-0" data-testid={`portal-available-slab-actions-${n}`}>
                          {item.spec_pdf && (
                            <a
                              data-testid={`portal-available-slab-pdf-${n}`}
                              href={`${API}/public/showroom/item/${item.spec_pdf}/pdf?slab=${encodeURIComponent(s.id)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="s-act s-act-soft"
                            >
                              <FilePdf size={15} weight="bold" /> PDF
                            </a>
                          )}
                          <button
                            type="button"
                            data-testid={`portal-available-slab-measure-${n}`}
                            onClick={() => setMeasure(s)}
                            disabled={!canMeasure}
                            title={canMeasure ? "Size a piece on this slab" : "Size not on file — ask us"}
                            className="s-act s-act-soft disabled:opacity-40"
                          >
                            <Ruler size={15} weight="bold" /> Measure
                          </button>
                          <button type="button" data-testid={`portal-available-slab-inquire-${n}`} onClick={() => requestSlab(s)} className="s-act s-act-gold">
                            <ChatCircleDots size={15} weight="bold" /> Inquire
                          </button>
                          <button
                            type="button"
                            data-testid={`portal-available-slab-wishlist-${n}`}
                            aria-pressed={isSaved}
                            onClick={() => wish(s)}
                            className={`s-act s-act-soft ${isSaved ? "s-act-on" : ""}`}
                          >
                            <Heart size={15} weight={isSaved ? "fill" : "bold"} /> {isSaved ? "Saved" : "Wishlist"}
                          </button>
                          <a data-testid={`portal-available-slab-email-${n}`} href={slabMail(s)} className="s-act s-act-soft">
                            <EnvelopeSimple size={15} weight="bold" /> Email
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </section>
        )}
      </div>

      {view3d && (
        <Slab3D photo={item.photo ? src(item.photo, 2400) : ""} lengthIn={item.length_in} widthIn={item.width_in} thickness={item.thickness} title={item.color} onClose={() => setView3d(false)} />
      )}
      {reserve && (
        <PortalReserveDialog
          key={reservePrefill?.note || "reserve"}
          item={{ id: item.id, kind: item.kind, label: item.color }}
          defaultNote={reservePrefill?.note || ""}
          defaultProject={reservePrefill?.project || ""}
          onClose={() => { setReserve(false); setReservePrefill(null); }}
          onDone={() => {
            setReserve(false);
            setReservePrefill(null);
            setItem((it) => ({ ...it, requested: true }));
          }}
        />
      )}
      {light >= 0 && shots.length > 0 && (
        <PortalLightbox shots={shots} index={light} srcOf={(path) => portalAsset(`/inventory/photo/${path}`)} onClose={() => setLight(-1)} />
      )}
      {slabShot && (
        <PortalLightbox prefix="portal-slab-lightbox" shots={slabShot.shots} index={slabShot.index} srcOf={(path) => portalAsset(`/inventory/photo/${path}`)} onClose={() => setSlabShot(null)} />
      )}
      {measure && (
        <SlabMeasure
          photo={measure.photo || item.photo ? src(measure.photo || item.photo, 1600) : ""}
          lengthIn={measure.length_in}
          widthIn={measure.width_in}
          polygon={item.kind === "Remnant" ? item.polygon : []}
          title={`${item.color} · Item ${measure.item_no || "—"}`}
          onClose={() => setMeasure(null)}
          onInquire={() => {
            const r = measure;
            setMeasure(null);
            requestSlab(r);
          }}
          onQuote={(piece) => {
            const r = measure;
            setMeasure(null);
            requestSlab(r, piece);
          }}
          onSave={(piece) => {
            const r = measure;
            setMeasure(null);
            setSaveCut({ target: r, piece });
          }}
        />
      )}
      {saveCut && (
        <SaveCutDialog
          item={item}
          target={saveCut.target}
          piece={saveCut.piece}
          projects={projects}
          onClose={() => setSaveCut(null)}
        />
      )}
    </PortalShell>
  );
}
